import type {
  DashboardAssistantEvent,
  DashboardEditOperation,
  DashboardFreshness,
  MetricValue,
  Provenance,
} from "@t3tools/contracts";

/**
 * One person's assistant thread for one dashboard, folded from what they sent
 * and the events that came back. Pure, so the panel and tests share it.
 */

export type DashboardAssistantUndo =
  | { readonly state: "idle" }
  | { readonly state: "pending" }
  | { readonly state: "done"; readonly version: number }
  | { readonly state: "failed"; readonly message: string };

export type DashboardAssistantItem =
  | { readonly kind: "user"; readonly id: string; readonly text: string }
  | {
      readonly kind: "message";
      readonly id: string;
      readonly text: string;
      readonly streaming: boolean;
    }
  | {
      readonly kind: "activity";
      readonly id: string;
      readonly text: string;
      readonly state: "running" | "done" | "failed";
    }
  | {
      readonly kind: "edits";
      readonly id: string;
      readonly dashboardRef: string;
      readonly version: number;
      readonly ops: ReadonlyArray<DashboardEditOperation>;
      readonly summary: string;
      readonly undo: DashboardAssistantUndo;
    }
  | {
      readonly kind: "answer";
      readonly id: string;
      readonly values: ReadonlyArray<MetricValue>;
      readonly provenance: ReadonlyArray<Provenance>;
      readonly freshness: DashboardFreshness;
    }
  | {
      readonly kind: "error";
      readonly id: string;
      readonly plain: string;
      readonly details?: string | undefined;
    };

export type DashboardAssistantThreadState = {
  readonly items: ReadonlyArray<DashboardAssistantItem>;
  /** The running turn: `turnId` is null until the send is accepted. */
  readonly running: { readonly turnId: string | null } | null;
};

/** One saved line of the thread, as the server keeps it. */
export type DashboardAssistantTimelineItem =
  | { readonly kind: "user"; readonly turnId: string; readonly text: string }
  | { readonly kind: "event"; readonly event: DashboardAssistantEvent };

export type DashboardAssistantThreadAction =
  | { readonly type: "history"; readonly timeline: ReadonlyArray<DashboardAssistantTimelineItem> }
  | { readonly type: "sent"; readonly id: string; readonly text: string }
  | { readonly type: "accepted"; readonly turnId: string }
  | { readonly type: "sendFailed"; readonly id: string; readonly message: string }
  | { readonly type: "event"; readonly event: DashboardAssistantEvent }
  | {
      readonly type: "undo";
      readonly itemId: string;
      readonly undo: DashboardAssistantUndo;
    };

export const EMPTY_DASHBOARD_ASSISTANT_THREAD: DashboardAssistantThreadState = {
  items: [],
  running: null,
};

/** Item ids are scoped by turn, since message and activity ids repeat across turns. */
function itemId(turnId: string, id: string): string {
  return `${turnId}:${id}`;
}

function upsert(
  items: ReadonlyArray<DashboardAssistantItem>,
  id: string,
  next: (current: DashboardAssistantItem | undefined) => DashboardAssistantItem,
): ReadonlyArray<DashboardAssistantItem> {
  const index = items.findIndex((item) => item.id === id);
  if (index === -1) return [...items, next(undefined)];
  return items.map((item, position) => (position === index ? next(item) : item));
}

function applyEvent(
  current: DashboardAssistantThreadState,
  event: DashboardAssistantEvent,
): DashboardAssistantThreadState {
  // A turn another page started (the home page hands a build to the dashboard)
  // is running here too, so Stop shows and a second message waits for it.
  const state: DashboardAssistantThreadState =
    current.running === null && event.type !== "done"
      ? { ...current, running: { turnId: event.turnId } }
      : current;
  const { items } = state;
  switch (event.type) {
    case "dashboard.created":
      // Pages react to this; the thread shows the activity lines that come with it.
      return state;
    case "message.delta": {
      const id = itemId(event.turnId, event.messageId);
      return {
        ...state,
        items: upsert(items, id, (current) => ({
          kind: "message",
          id,
          text: (current?.kind === "message" ? current.text : "") + event.delta,
          streaming: true,
        })),
      };
    }
    case "message.done": {
      const id = itemId(event.turnId, event.messageId);
      return {
        ...state,
        items: upsert(items, id, () => ({
          kind: "message",
          id,
          text: event.text,
          streaming: false,
        })),
      };
    }
    case "activity": {
      const id = itemId(event.turnId, event.activityId);
      return {
        ...state,
        items: upsert(items, id, () => ({
          kind: "activity",
          id,
          text: event.text,
          state: event.state,
        })),
      };
    }
    case "edits.applied": {
      const id = itemId(event.turnId, `edits-${event.version}`);
      return {
        ...state,
        items: upsert(items, id, () => ({
          kind: "edits",
          id,
          dashboardRef: event.dashboardRef,
          version: event.version,
          ops: event.ops,
          summary: event.summary,
          undo: { state: "idle" },
        })),
      };
    }
    case "answer": {
      const id = itemId(event.turnId, `answer-${items.length}`);
      return {
        ...state,
        items: [
          ...items,
          {
            kind: "answer",
            id,
            values: event.values,
            provenance: event.provenance,
            freshness: event.freshness,
          },
        ],
      };
    }
    case "error": {
      const id = itemId(event.turnId, `error-${items.length}`);
      return {
        ...state,
        items: [...items, { kind: "error", id, plain: event.plain, details: event.details }],
      };
    }
    case "done": {
      // A turn that ends leaves nothing streaming or running behind.
      const prefix = `${event.turnId}:`;
      const settled = items.map((item): DashboardAssistantItem => {
        if (!item.id.startsWith(prefix)) return item;
        if (item.kind === "message" && item.streaming) return { ...item, streaming: false };
        if (item.kind === "activity" && item.state === "running") {
          return { ...item, state: event.reason === "completed" ? "done" : "failed" };
        }
        return item;
      });
      const running =
        state.running?.turnId === event.turnId || state.running?.turnId === null
          ? null
          : state.running;
      return { items: settled, running };
    }
    case "preview":
      // Previews are drawn on the dashboard, not in the thread.
      return state;
  }
}

/** The turn an item belongs to; items the page made itself carry no turn. */
function turnOf(item: DashboardAssistantItem): string {
  const colon = item.id.indexOf(":");
  return colon === -1 ? "" : item.id.slice(0, colon);
}

/**
 * Replays a saved thread. Turns that were saved are drawn from the save; items
 * from turns the page has already seen live and the person's own just-sent
 * messages stay after them. A saved turn that never finished (the service
 * restarted mid-turn) is closed as failed, unless it is the turn running now.
 */
function applyHistory(
  state: DashboardAssistantThreadState,
  timeline: ReadonlyArray<DashboardAssistantTimelineItem>,
): DashboardAssistantThreadState {
  let replay: DashboardAssistantThreadState = { items: [], running: null };
  const turns = new Set<string>();
  const finished = new Set<string>();
  for (const line of timeline) {
    if (line.kind === "user") {
      turns.add(line.turnId);
      replay = {
        ...replay,
        items: [...replay.items, { kind: "user", id: `${line.turnId}:user`, text: line.text }],
      };
      continue;
    }
    turns.add(line.event.turnId);
    if (line.event.type === "done") finished.add(line.event.turnId);
    replay = applyEvent(replay, line.event);
  }
  const liveTurn = state.running?.turnId ?? null;
  const items = replay.items.map((item): DashboardAssistantItem => {
    const turnId = turnOf(item);
    if (finished.has(turnId) || turnId === liveTurn) return item;
    if (item.kind === "message" && item.streaming) return { ...item, streaming: false };
    if (item.kind === "activity" && item.state === "running") return { ...item, state: "failed" };
    return item;
  });
  const live = state.items.filter((item) => {
    return !turns.has(turnOf(item));
  });
  return { items: [...items, ...live], running: state.running };
}

export function dashboardAssistantThreadReducer(
  state: DashboardAssistantThreadState,
  action: DashboardAssistantThreadAction,
): DashboardAssistantThreadState {
  switch (action.type) {
    case "history":
      return applyHistory(state, action.timeline);
    case "sent":
      return {
        items: [...state.items, { kind: "user", id: action.id, text: action.text }],
        running: { turnId: null },
      };
    case "accepted":
      return state.running ? { ...state, running: { turnId: action.turnId } } : state;
    case "sendFailed":
      return {
        items: [...state.items, { kind: "error", id: action.id, plain: action.message }],
        running: null,
      };
    case "event":
      return applyEvent(state, action.event);
    case "undo":
      return {
        ...state,
        items: state.items.map((item) =>
          item.id === action.itemId && item.kind === "edits"
            ? { ...item, undo: action.undo }
            : item,
        ),
      };
  }
}

/** The widgets an edit added or changed, for the brief highlight. Removed widgets are gone. */
export function editedWidgetIds(ops: ReadonlyArray<DashboardEditOperation>): ReadonlySet<string> {
  const ids = new Set<string>();
  for (const op of ops) {
    if (op.op === "add_widget") ids.add(op.widget.id);
    else if (op.op === "remove_widget") ids.delete(op.widgetId);
    else if ("widgetId" in op) ids.add(op.widgetId);
  }
  return ids;
}
